import NeedsAnalysisModal from '@/components/NeedsAnalysisModal'
import ReportBugModal from '@/components/ReportBugModal'
import { AccountDialog, type AccountDialogTab } from '@/components/account/AccountDialog'
import { DesktopNav } from '@/components/header/DesktopNav'
import { MobileNav } from '@/components/header/MobileNav'
import { UserButtonMenu } from '@/components/header/UserButtonMenu'
import { NotificationBell } from '@/components/notifications/NotificationBell'
import { useFeaturebaseChangelogWidget } from '@/hooks/useFeaturebaseChangelogWidget'
import { useFeaturebaseWidget } from '@/hooks/useFeaturebaseWidget'
import { useHeaderNavigation } from '@/hooks/useHeaderNavigation'
import { useIsEngenStaff } from '@/hooks/useIsEngenStaff'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useCoachingPortalEntry } from '@/hooks/useCoachingPortalEntry'
import { ADMIN_PORTAL_PATH, useHasAdminPortalAccess } from '@/hooks/useHasAdminPortalAccess'
import { useRole } from '@/hooks/useRole'
import { cn } from '@/lib/utils'
import { ClerkUserRole } from '@/types/auth'
import { SignedIn, SignedOut, SignInButton, useUser } from '@clerk/clerk-react'
import { Menu, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { mutate } from 'swr'
import { getApiUrl } from '@/config/api'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useLanguageSync } from '@/hooks/useLanguageSync'

type Props = {
  readonly fixed?: boolean
}

// Userpilot anchors for the tour's menu steps. The phone and desktop headers are
// both in the DOM (one hidden by CSS), so each anchor goes only on the copy that
// is on screen; a step then needs one selector that works on every device.
const USERPILOT_NAV_MENU = 'nav-menu'
const USERPILOT_USER_MENU = 'nav-user-menu'

export default function Header({ fixed = true }: Props) {
  const { user } = useUser()
  const { t } = useTranslation()
  const { userRole, hasRole } = useRole()
  const { navLinks, isLinkActive, isSubLinkActive } = useHeaderNavigation()
  const isMobile = useIsMobile()
  const [needsAnalysisOpen, setNeedsAnalysisOpen] = useState(false)
  const [reportBugOpen, setReportBugOpen] = useState(false)
  const [accountDialogOpen, setAccountDialogOpen] = useState(false)
  const [accountDialogTab, setAccountDialogTab] = useState<AccountDialogTab>('profile')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const isEngenStaff = useIsEngenStaff()
  const [scrolledDown, setScrolledDown] = useState(false)
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null)

  // Callback to handle when needs analysis modal closes after successful submission
  const handleNeedsAnalysisOpenChange = useCallback(
    (open: boolean) => {
      if (!open && needsAnalysisOpen) {
        // Modal is closing - invalidate the recommendations cache to fetch fresh data
        // This ensures recommended courses are refreshed with the newest recommendations first
        mutate(getApiUrl('/recommendations/courses'))
      }
      setNeedsAnalysisOpen(open)
    },
    [needsAnalysisOpen]
  )

  useEffect(() => {
    const handleScroll = () => {
      setScrolledDown(window.scrollY > 0)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Who gets the *Admin Portal* entry, and where it lands. Both live next to each
  // other on the hook because the coach portal's own identity menu asks the same
  // questions — it has no header, so it carries the way back out (ENG-3568), and
  // the two must not drift.
  const hasAdminAccess = useHasAdminPortalAccess()

  // The *Coaching Portal* entry, beside Admin Portal in the same menu (ENG-3568).
  // Who is offered it and where it lands live in the hook, which is the one place
  // that answers "how do you get into the coach portal".
  //
  // The seam that leaves until FE-1a retires `coach-workspace`: a coach outside
  // the pilot sees the entry and `CoachWorkspaceGuard` bounces them off the page
  // behind it. Accepted rather than papered over — the flag is targeted per user
  // at the pilot coaches, and FE-1a removes the guard rather than re-adding a flag
  // check here.
  const coachingPortal = useCoachingPortalEntry()

  // Changelog widget surfaces only for admins (EnGen-side + customer admins).
  // Gates both the menu entry and the underlying hook so the auto-pop card
  // doesn't appear for learners/instructors/coaches either.
  const canSeeChangelog =
    userRole === ClerkUserRole.SUPER_ADMIN ||
    userRole === ClerkUserRole.ENGEN_ADMIN ||
    userRole === ClerkUserRole.CLIENT_ADMIN

  // Each Featurebase widget gets its own user derivation. They intentionally
  // scope to different audiences (feedback → EnGen staff; changelog → all
  // admins). Sharing a single `featurebaseUser` would silently narrow the
  // feedback widget to admins-only if the feedback menu entry is ever shown
  // to a non-admin role.
  const featurebaseUserBase =
    user?.id && user?.fullName && user?.primaryEmailAddress?.emailAddress
      ? { email: user.primaryEmailAddress.emailAddress, name: user.fullName, userId: user.id }
      : undefined

  // Same predicate as Report Bug and the backend feedback JWT: EnGen-org
  // membership, not a role list. Granting `coach` removes `engen_admin` in
  // Clerk but leaves the user in the EnGen org, so they keep the widget
  // (ENG-3569). Deliberately not `canSeeChangelog`, which stays closed to coaches.
  const feedbackUser = isEngenStaff ? featurebaseUserBase : undefined
  const changelogUser = canSeeChangelog ? featurebaseUserBase : undefined

  const { initialize: initializeFeaturebase, open: openFeaturebase } = useFeaturebaseWidget(feedbackUser)
  const { open: openChangelog, unreadCount: changelogUnreadCount } = useFeaturebaseChangelogWidget(changelogUser)

  const handleOpenFeedback = useCallback(async () => {
    await initializeFeaturebase()
    openFeaturebase()
  }, [initializeFeaturebase, openFeaturebase])

  const handleMobileMenuEscape = useCallback(() => {
    setMobileMenuOpen(false)
    requestAnimationFrame(() => {
      mobileMenuButtonRef.current?.focus()
    })
  }, [])

  const openAccountDialog = useCallback((tab: AccountDialogTab) => {
    setAccountDialogTab(tab)
    setAccountDialogOpen(true)
  }, [])

  useLanguageSync()

  return (
    <>
      <a
        href='#main-content'
        className='sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-white focus:text-brand-standard focus:rounded focus:shadow-lg focus:font-semibold focus:outline focus:outline-2 focus:outline-brand-standard'
      >
        {t('header.skipToMainContent')}
      </a>
      <header
        className={cn('scrollable-top:shadow-md px-4 py-3 transition duration-300 bg-background', {
          'fixed top-0 right-0 left-0 z-50': fixed,
          // Static headers still need a stacking context, or content scrolling
          // underneath (e.g. the admin panels) paints over the shadow.
          'relative z-50': !fixed,
          'shadow-md': !fixed || scrolledDown,
        })}
      >
        {/* Mobile Layout */}
        <div className='flex items-center justify-between md:hidden'>
          <SignedIn>
            {hasRole ? (
              <button
                type='button'
                ref={mobileMenuButtonRef}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className='rounded-md p-2 text-gray-700 transition-colors hover:bg-gray-100'
                aria-label={t('header.toggleMenu')}
                aria-expanded={mobileMenuOpen}
                data-userpilot={isMobile ? USERPILOT_NAV_MENU : undefined}
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            ) : (
              <div className='w-10' />
            )}
          </SignedIn>
          <SignedOut>
            <div className='w-10' />
          </SignedOut>

          <Link to='/' className='flex-shrink-0'>
            <img src='/engen-logo.png' alt='EnGen Logo' className='h-8' />
          </Link>

          <SignedIn>
            <div className='flex items-center gap-1'>
              <NotificationBell onOpenSettings={() => openAccountDialog('notifications')} />
              <span className='inline-flex' data-userpilot={isMobile ? USERPILOT_USER_MENU : undefined}>
                <UserButtonMenu
                  hasRole={hasRole}
                  hasAdminAccess={hasAdminAccess}
                  adminPath={ADMIN_PORTAL_PATH}
                  hasCoachingPortalAccess={coachingPortal.isAvailable}
                  coachingPortalPath={coachingPortal.path}
                  onNeedsAnalysisOpen={() => setNeedsAnalysisOpen(true)}
                  onFeedbackOpen={handleOpenFeedback}
                  isEngenStaff={isEngenStaff}
                  onReportBugOpen={() => setReportBugOpen(true)}
                  onChangelogOpen={openChangelog}
                  changelogUnreadCount={changelogUnreadCount}
                  onManageAccountOpen={() => openAccountDialog('profile')}
                  canSeeChangelog={canSeeChangelog}
                />
              </span>
            </div>
          </SignedIn>

          <SignedOut>
            <SignInButton mode='modal'>
              <button
                type='button'
                className='rounded bg-(--brand-standard) px-3 py-1.5 text-sm text-white transition-colors duration-200 hover:bg-(--brand-standard-dark)'
              >
                {t('header.signIn')}
              </button>
            </SignInButton>
          </SignedOut>
        </div>

        {/* Desktop Layout */}
        <div className='hidden items-center justify-between md:flex px-6 mx-auto max-w-7xl'>
          <div className='flex items-center'>
            <Link to='/' className='group'>
              <img
                src='/engen-logo.png'
                alt='EnGen Logo'
                className='h-8 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3'
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <SignedIn>
            {hasRole && (
              <DesktopNav
                navLinks={navLinks}
                isLinkActive={isLinkActive}
                isSubLinkActive={isSubLinkActive}
                userpilotAnchor={isMobile ? undefined : USERPILOT_NAV_MENU}
              />
            )}
          </SignedIn>

          {/* Desktop Right Side */}
          <div className='flex items-center space-x-4'>
            <SignedIn>
              <div className='flex flex-col items-end'>
                <div className='text-sm text-gray-600'>{t('header.hello', { name: user?.fullName || 'User' })}</div>
              </div>
              {/* Renders nothing, and makes no request, while the Coach Workspace
                  flag is off. */}
              <NotificationBell onOpenSettings={() => openAccountDialog('notifications')} />
              <span className='inline-flex' data-userpilot={isMobile ? undefined : USERPILOT_USER_MENU}>
                <UserButtonMenu
                  hasRole={hasRole}
                  hasAdminAccess={hasAdminAccess}
                  adminPath={ADMIN_PORTAL_PATH}
                  hasCoachingPortalAccess={coachingPortal.isAvailable}
                  coachingPortalPath={coachingPortal.path}
                  onNeedsAnalysisOpen={() => setNeedsAnalysisOpen(true)}
                  onFeedbackOpen={handleOpenFeedback}
                  isEngenStaff={isEngenStaff}
                  onReportBugOpen={() => setReportBugOpen(true)}
                  onChangelogOpen={openChangelog}
                  canSeeChangelog={canSeeChangelog}
                  changelogUnreadCount={changelogUnreadCount}
                  onManageAccountOpen={() => openAccountDialog('profile')}
                />
              </span>
            </SignedIn>

            <SignedOut>
              <SignInButton mode='modal'>
                <button
                  type='button'
                  className='rounded bg-(--brand-standard) px-4 py-2 text-sm text-white transition-colors duration-200 hover:bg-(--brand-standard-dark)'
                >
                  {t('header.signIn')}
                </button>
              </SignInButton>
            </SignedOut>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <SignedIn>
          {hasRole && (
            <MobileNav
              navLinks={navLinks}
              isLinkActive={isLinkActive}
              isSubLinkActive={isSubLinkActive}
              isOpen={mobileMenuOpen}
              onNavigate={() => setMobileMenuOpen(false)}
              onEscape={handleMobileMenuEscape}
            />
          )}
        </SignedIn>

        <NeedsAnalysisModal open={needsAnalysisOpen} onOpenChange={handleNeedsAnalysisOpenChange} />
        <ReportBugModal open={reportBugOpen} onOpenChange={setReportBugOpen} />
        <AccountDialog
          open={accountDialogOpen}
          onOpenChange={setAccountDialogOpen}
          onNeedsAnalysisOpen={() => setNeedsAnalysisOpen(true)}
          initialTab={accountDialogTab}
        />
      </header>
    </>
  )
}
